namespace SaCartUtils {
    export const LOGIN_MODAL_ID = "loginModal";

    export enum Selectors {
        ButtonCounterMinus = ".btn-counter.minus",
        ButtonCounterPlus = ".btn-counter.plus",
        ItemCountDisplay = ".itemCountDisplay",
        JsCartQuantity = ".js-cart-quantity",
        ViewCartBtn = "#viewCartBtn",
    }

    export const MIN_QUANTITY = 1;
    export const MAX_QUANTITY = 24;

    // #region GetCart

    export interface CartResponseWithWrapper {
        cart: CartResponse;
    }

    export interface CartResponse {
        id: number;
        memberId: number;
        totalPrice: number;
        cartItems: CartItemResponse[];
    }

    export interface CartItemResponse {
        id: string;
        productId: string;
        productType: string;
        description: string;
        customProperties: string;
        quantity: number;
        price: number;
    }

    export function initializeLoginModal(): bootstrap.Modal | null {
        const loginModalEl = document.getElementById(LOGIN_MODAL_ID);

        if (loginModalEl) {
            const loginModalInstance = new bootstrap.Modal(loginModalEl, {
                keyboard: false,
            });
            return loginModalInstance;
        }

        console.error(
            "Unable to find login modal element with id:",
            LOGIN_MODAL_ID,
        );
        return null;
    }

    export function showLoginModal(): bootstrap.Modal | null {
        const loginModal = initializeLoginModal();

        if (loginModal) {
            loginModal.show();
        }

        return loginModal;
    }

    function processCartResponse(data: CartResponse | null | undefined) {
        $(Selectors.JsCartQuantity).hide();
        try {
            if (data?.cartItems?.length && data.cartItems.length > 0) {
                const cartQuantity = data.cartItems.reduce(
                    (total, item) => total + item.quantity,
                    0,
                );
                $(Selectors.JsCartQuantity).text(cartQuantity);
                $(Selectors.JsCartQuantity).show();
            }
        } catch {
            // nothing to do
        }
    }

    export function loadCart() {
        const cartApiUrl = SaNavigationUtils.getBaseUrl() + "/api/Cart/Cart";
        const ajaxSettings = {
            cache: false,
            url: cartApiUrl,
            method: "POST",
            xhrFields: {
                withCredentials: true,
            },
        };

        $.ajax(ajaxSettings).done(function (
            data: CartResponseWithWrapper | null | undefined,
        ) {
            processCartResponse(data?.cart);
        });
    }

    export interface ShowCartPopupOptions {
        showPriceDescription?: boolean;
        showQuantityControls?: boolean;
    }

    export function showCartPopup(
        description: string,
        isMember: boolean,
        price: number,
        originalPrice: number,
        icon: string,
        itemAddedToCart: CartItemResponse,
        options: ShowCartPopupOptions = {},
    ) {
        const { showPriceDescription = true, showQuantityControls = true } =
            options;
        $(".js-cart-title").text(description);
        let priceDescription = "Non-Member Price";
        if (isMember) {
            priceDescription = "Member Price";
        }
        if (isMember && originalPrice > price) {
            $(".js-cart-original-price").parent().show().addClass("d-block");
        } else {
            $(".js-cart-original-price").parent().hide().removeClass("d-block");
        }
        $(".js-cart-price-description")
            .text(priceDescription)
            .toggle(showPriceDescription);
        $(".js-cart-price").text(price);
        $(".js-cart-original-price").text(originalPrice);
        // Show or hide the cart icon based on whether an icon URL is provided
        const $cartIcon = $(".js-cart-icon");
        if (icon) {
            $cartIcon.attr("src", icon).show();
        } else {
            $cartIcon.removeAttr("src").hide();
        }

        let $modalCartCard = $("#cartModal .wCartModalWidget__cart-item-card");

        if ($modalCartCard.length == 0) {
            $modalCartCard = $(".wCartModalWidget__cart-item");
        }

        const $counter = $("#cartModal [data-cart-counter]");
        $counter.closest(".quantity-row").toggle(showQuantityControls);
        const quantity = itemAddedToCart?.quantity ?? 1;

        $modalCartCard.attr("data-cartitemid", itemAddedToCart?.id ?? "");
        $modalCartCard.attr("data-productid", itemAddedToCart?.productId ?? "");
        $modalCartCard.attr(
            "data-producttype",
            itemAddedToCart?.productType ?? "",
        );
        $modalCartCard.attr(
            "data-description",
            itemAddedToCart?.description ?? description ?? "",
        );
        $modalCartCard.attr(
            "data-customproperties",
            itemAddedToCart?.customProperties ?? "",
        );

        $counter
            .find(".btn-counter.minus, .btn-counter.plus")
            .attr("data-cartitemid", itemAddedToCart?.id ?? "");
        setQuantity($counter, quantity);
        $counter.data(
            "lastSyncedQuantity",
            globalThis.SaCartUtils.getQuantity($counter),
        );

        const modalElement = document.getElementById("cartModal");
        const modal = modalElement
            ? bootstrap.Modal.getOrCreateInstance(modalElement)
            : null;
        loadCart();
        modal?.show();

        // GA4 tracking
        const dataLayerItem: SaAnalyticsUtils.DataLayerCheckoutItem = {
            item_id: $modalCartCard.data("productid"),
            item_language: undefined, // Maybe someday?
            item_name: $modalCartCard.data("description"),
            price: isMember ? price : originalPrice,
            quantity: quantity,
        };
        SaAnalyticsUtils.addToDataLayer(
            SaAnalyticsUtils.buildViewCartEventFromItems([dataLayerItem]),
        );
    }

    // #endregion

    // #region AddToCart

    export interface AddToCartRequest {
        productId: number;
        cartDescription: string;
        quantity: number;
        isGift?: boolean;
        customProperties?: string;
    }

    export interface AddToCartApiResponse {
        success: boolean;
        isMember: boolean;
        icon?: string;
        itemAddedToCart: CartItemResponse;
    }

    export interface AddToCartOptions {
        /** Falls back to the cart-returned price if not supplied. */
        originalPrice?: number;
        onSuccess?: (data: AddToCartApiResponse) => void;
        /** Always runs after success or error — put spinner-hiding here. */
        onComplete?: () => void;
    }

    /**
     * Sends an add-to-cart request to the default cart API endpoint, then shows the
     * cart popup on success or an error alert on failure. Callers remain responsible
     * for their own field validation before calling this (e.g. missing productId),
     * spinner show/hide timing outside the request lifecycle, and any analytics push
     * via onSuccess.
     */
    export function addToCart(
        request: AddToCartRequest,
        options: AddToCartOptions = {},
    ): void {
        $.ajax({
            type: "post",
            url: "/api/Cart/Add",
            contentType: "application/json",
            data: JSON.stringify(request),
            success: function (data: AddToCartApiResponse) {
                if (data?.success) {
                    const originalPrice =
                        options.originalPrice ?? data.itemAddedToCart.price;
                    showCartPopup(
                        data.itemAddedToCart.description,
                        data.isMember,
                        data.itemAddedToCart.price,
                        originalPrice,
                        data.icon ?? "",
                        data.itemAddedToCart,
                    );
                    options.onSuccess?.(data);
                } else {
                    SaAlertUtils.defaultFire(
                        "Add to Cart Error",
                        "Unable to add item to cart. Please try again later.",
                    );
                }
                options.onComplete?.();
            },
            error: function (xhr: JQueryXHR) {
                SaAlertUtils.defaultFireFromResponse("Add to Cart Error", xhr);
                options.onComplete?.();
            },
        });
    }

    // #endregion

    // #region UpdateCart

    /**
     * Updates the cart counter button states and the view-cart button based on the current quantity and sync state.
     */
    function setCounterControlsSyncingState(
        $counter: JQuery,
        isSyncing: boolean,
        getQuantityFn: (counterElement: JQuery) => number,
    ): void {
        const quantity = getQuantityFn($counter);
        const $minusBtn = $counter.find(Selectors.ButtonCounterMinus);
        const $plusBtn = $counter.find(Selectors.ButtonCounterPlus);

        $(Selectors.ViewCartBtn).prop("disabled", isSyncing);
        $minusBtn.prop("disabled", quantity <= MIN_QUANTITY);
        $plusBtn.prop("disabled", quantity >= MAX_QUANTITY);
    }

    export function clampQuantity(value: number): number {
        if (Number.isNaN(value)) {
            return MIN_QUANTITY;
        }

        return Math.max(MIN_QUANTITY, Math.min(MAX_QUANTITY, value));
    }

    /**
     * Reads the displayed quantity from a cart counter and returns a normalized value within the allowed range.
     */
    export function getQuantity($counter: JQuery): number {
        const $display = $counter.find(Selectors.ItemCountDisplay);
        const displayValue = $display.is("input")
            ? $display.val()
            : $display.text();
        const rawValue = SaNumberUtils.coerceToInteger(displayValue);

        return clampQuantity(rawValue);
    }

    /**
     * Writes a quantity to the cart counter UI and keeps the increment and decrement controls in sync.
     */
    export function setQuantity($counter: JQuery, quantity: number): void {
        const safeQuantity = clampQuantity(quantity);
        const $display = $counter.find(Selectors.ItemCountDisplay);
        const $minusBtn = $counter.find(Selectors.ButtonCounterMinus);
        const $plusBtn = $counter.find(Selectors.ButtonCounterPlus);

        if ($display.is("input")) {
            $display.val(safeQuantity);
        } else {
            $display.text(safeQuantity);
        }

        $minusBtn.prop("disabled", safeQuantity <= MIN_QUANTITY);
        $plusBtn.prop("disabled", safeQuantity >= MAX_QUANTITY);
    }

    export interface CartItemUpdatePayload {
        cartItemId: string;
        quantity: number;
        isGift: boolean;
        productId: string;
        productType: string;
        description: string;
        customProperties: string;
    }

    /**
     * Builds the default payload used to submit a cart item quantity update.
     */
    function buildDefaultPayload(
        $counter: JQuery,
        quantity: number,
        options: Pick<BindCountersOptions, "itemSelector" | "giftSelector">,
    ): CartItemUpdatePayload | undefined {
        const $item = $counter.closest(options.itemSelector);
        if ($item.length === 0) {
            console.error(
                "Unable to find cart item for quantity counter. Cart item update cannot proceed. itemSelector:",
                options.itemSelector,
            );
            return;
        }
        return {
            cartItemId: $item.data("cartitemid"),
            quantity: quantity,
            isGift: $item.parent().find(options.giftSelector).is(":checked"), // parent feels like we might grab a different cartItem, but there must be a wrapper for cart items and the gift selector is part of this wrapper.
            productId: $item.data("productid"),
            productType: $item.data("producttype"),
            description: $item.data("description"),
            customProperties: $item.data("customproperties"),
        };
    }

    export interface CartTotalsResponse {
        success: boolean;
        subtotal: number;
        subtotalText: string;
        memberDiscount: number;
        memberDiscountText: string;
        total: number;
        totalText: string;
        itemCount: number;
    }

    /**
     * Sends the cart item update request using the default cart API endpoint.
     */
    function syncDefault(
        $counter: JQuery,
        quantity: number,
        options: Pick<BindCountersOptions, "itemSelector" | "giftSelector">,
    ): JQuery.jqXHR<CartTotalsResponse> {
        const data = buildDefaultPayload($counter, quantity, options);
        if (!data) {
            throw new Error("Sync Payload is empty!");
        }
        return $.ajax({
            type: "post",
            url: "/api/Cart/UpdateItem",
            data,
        });
    }

    export interface BindCountersOptions {
        namespace: string;
        counterSelector: string;
        cardSelector: string;
        itemSelector: string;
        giftSelector: string;
        debounceMs: number;
        skipSync?: boolean;
        onQuantityApplied?:
            ((counterElement: JQuery, quantity: number) => void) | null;
        onSyncSuccess?:
            | ((
                  counterElement: JQuery,
                  quantity: number,
                  response: CartTotalsResponse,
              ) => void)
            | null;
        onSyncFailed?:
            ((counterElement: JQuery, quantity: number) => void) | null;
    }

    enum BindCounterDataKey {
        DebounceTimer = "debounceTimer",
        ForceSync = "forceSync",
        LastSyncedQuantity = "lastSyncedQuantity",
        PendingQuantity = "pendingQuantity",
        Syncing = "syncing",
    }

    /**
     * Binds click and change handlers to quantity counters and gift purchase checkboxes within the cart, enabling automatic synchronization with the server when changes are made.
     * The synchronization is debounced to prevent excessive server calls during rapid interactions. Skipping synchronization logic and event handlers can be provided through the options parameter.
     * Note that while this function returns an object with an initialize method, the event handlers are bound immediately upon invocation,
     * and the initialize method can be used to apply initial quantities to counters within a specific root element if needed to run again.
     */
    export function bindCounters(options: Partial<BindCountersOptions>): {
        initialize: (rootElement: JQuery<Document>) => void;
    } {
        const defaultOptions: BindCountersOptions = {
            namespace: ".saCartQty",
            counterSelector: "[data-cart-counter]",
            cardSelector:
                ".wCartWidget__cart-item-card, .wCartModalWidget__cart-item-card",
            itemSelector:
                ".wCartWidget__cart-item, .wCartModalWidget__cart-item",
            giftSelector: ".js-confirm-gift-purchase",
            skipSync: false,
            debounceMs: 400,
            onQuantityApplied: null,
            onSyncSuccess: null,
            onSyncFailed: null,
        };

        const settings = $.extend(defaultOptions, options ?? {});

        if (
            settings.skipSync &&
            (typeof settings.onSyncSuccess === "function" ||
                typeof settings.onSyncFailed === "function")
        ) {
            console.error(
                "SaCartUtils.bindCounters: Sync success and failure handlers will not be called when skipSync is true.",
            );
        }

        /**
         * Applies the current displayed quantity to every counter in the provided root element.
         */
        function initialize($root: JQuery<Document>): void {
            $root.find(settings.counterSelector).each(function () {
                const $counter = $(this);
                const initialQuantity = getQuantity($counter);
                setQuantity($counter, initialQuantity);
                $counter.data(
                    BindCounterDataKey.LastSyncedQuantity,
                    initialQuantity,
                );
                $counter.data(
                    BindCounterDataKey.PendingQuantity,
                    initialQuantity,
                );

                if (typeof settings.onQuantityApplied === "function") {
                    settings.onQuantityApplied($counter, initialQuantity);
                }
            });
        }

        /**
         * Synchronizes a counter's pending quantity with the server, rolling back on failure.
         */
        function syncNow($counter: JQuery, previousQuantity: number): void {
            if (!settings.skipSync) {
                if ($counter.data(BindCounterDataKey.Syncing) === true) {
                    return;
                }

                const shouldForceSync =
                    $counter.data(BindCounterDataKey.ForceSync) === true;
                $counter.data(BindCounterDataKey.ForceSync, false);
                const desiredQuantity = clampQuantity(
                    Number.parseInt(
                        $counter.data(BindCounterDataKey.PendingQuantity),
                        10,
                    ),
                );
                const currentSyncedQuantity = clampQuantity(
                    Number.parseInt(
                        $counter.data(BindCounterDataKey.LastSyncedQuantity),
                        10,
                    ),
                );

                if (
                    !shouldForceSync &&
                    desiredQuantity === currentSyncedQuantity
                ) {
                    setCounterControlsSyncingState(
                        $counter,
                        false,
                        getQuantity,
                    );
                    return;
                }

                $counter.data(BindCounterDataKey.Syncing, true);
                setCounterControlsSyncingState($counter, true, getQuantity);

                syncDefault($counter, desiredQuantity, {
                    itemSelector: settings.itemSelector,
                    giftSelector: settings.giftSelector,
                })
                    .done(function (response) {
                        if (response?.success) {
                            $counter.data(
                                BindCounterDataKey.LastSyncedQuantity,
                                desiredQuantity,
                            );
                            if (typeof settings.onSyncSuccess === "function") {
                                settings.onSyncSuccess(
                                    $counter,
                                    desiredQuantity,
                                    response,
                                );
                            }
                            return;
                        }

                        setQuantity($counter, previousQuantity);
                        $counter.data(
                            BindCounterDataKey.PendingQuantity,
                            previousQuantity,
                        );
                        $counter.data(
                            BindCounterDataKey.LastSyncedQuantity,
                            previousQuantity,
                        );
                        if (typeof settings.onQuantityApplied === "function") {
                            settings.onQuantityApplied(
                                $counter,
                                previousQuantity,
                            );
                        }
                        if (typeof settings.onSyncFailed === "function") {
                            settings.onSyncFailed($counter, previousQuantity);
                        }
                    })
                    .fail(function () {
                        setQuantity($counter, previousQuantity);
                        $counter.data(
                            BindCounterDataKey.PendingQuantity,
                            previousQuantity,
                        );
                        $counter.data(
                            BindCounterDataKey.LastSyncedQuantity,
                            previousQuantity,
                        );
                        if (typeof settings.onQuantityApplied === "function") {
                            settings.onQuantityApplied(
                                $counter,
                                previousQuantity,
                            );
                        }
                        if (typeof settings.onSyncFailed === "function") {
                            settings.onSyncFailed($counter, previousQuantity);
                        }
                    })
                    .always(function () {
                        $counter.data(BindCounterDataKey.Syncing, false);
                        setCounterControlsSyncingState(
                            $counter,
                            false,
                            getQuantity,
                        );

                        const forceSyncQueued =
                            $counter.data(BindCounterDataKey.ForceSync) ===
                            true;
                        const latestPending = clampQuantity(
                            Number.parseInt(
                                $counter.data(
                                    BindCounterDataKey.PendingQuantity,
                                ),
                                10,
                            ),
                        );
                        const latestSynced = clampQuantity(
                            Number.parseInt(
                                $counter.data(
                                    BindCounterDataKey.LastSyncedQuantity,
                                ),
                                10,
                            ),
                        );
                        if (forceSyncQueued || latestPending !== latestSynced) {
                            scheduleSync($counter, latestSynced);
                        }
                    });
            }
        }

        /**
         * Schedules a debounced sync for the supplied counter.
         */
        function scheduleSync(
            $counter: JQuery<HTMLElement>,
            previousQuantity: number,
        ): void {
            const existingTimer = $counter.data(
                BindCounterDataKey.DebounceTimer,
            );
            if (existingTimer) {
                clearTimeout(existingTimer);
            }

            const timer = setTimeout(function () {
                $counter.removeData(BindCounterDataKey.DebounceTimer);
                syncNow($counter, previousQuantity);
            }, settings.debounceMs);

            $("#viewCartBtn").prop("disabled", true);
            $counter.data(BindCounterDataKey.DebounceTimer, timer);
        }

        /**
         * Stores a new pending quantity, updates the UI, and queues synchronization when needed.
         */
        function queueSync(
            $counter: JQuery<HTMLElement>,
            nextQuantity: number,
            previousQuantity: number,
            forceSync?: boolean,
        ): void {
            const safeNextQuantity = clampQuantity(nextQuantity);

            setQuantity($counter, safeNextQuantity);
            $counter.data(BindCounterDataKey.PendingQuantity, safeNextQuantity);
            if (forceSync === true) {
                $counter.data(BindCounterDataKey.ForceSync, true);
            }

            if (typeof settings.onQuantityApplied === "function") {
                settings.onQuantityApplied($counter, safeNextQuantity);
            }

            if ($counter.data(BindCounterDataKey.Syncing) === true) {
                console.warn("Cart already syncing...");
                return;
            }

            if (settings.skipSync === true) {
                const existingTimer = $counter.data(
                    BindCounterDataKey.DebounceTimer,
                );
                if (existingTimer) {
                    clearTimeout(existingTimer);
                }

                $counter.removeData(BindCounterDataKey.DebounceTimer);
                $(Selectors.ViewCartBtn).prop("disabled", false);
                return;
            }

            scheduleSync($counter, previousQuantity);
        }

        /**
         * Finds the first cart counter element within a cart item card.
         */
        function findCounterInCard(
            $card: JQuery<HTMLElement>,
        ): JQuery<HTMLElement> {
            const $scopedCounter = $card.find(settings.counterSelector).first();
            if ($scopedCounter.length > 0) {
                return $scopedCounter;
            }

            // Fallback for selectors that include an ancestor qualifier (e.g. "#cartModal [data-cart-counter]").
            return $card.find("[data-cart-counter]").first();
        }

        initialize($(document));

        $(document).off(
            "click" + settings.namespace,
            settings.counterSelector + " " + Selectors.ButtonCounterPlus,
        );
        $(document).on(
            "click" + settings.namespace,
            settings.counterSelector + " " + Selectors.ButtonCounterPlus,
            function () {
                const $counter = $(this).closest(settings.counterSelector);
                const currentValue = getQuantity($counter);

                if (currentValue >= MAX_QUANTITY) {
                    setQuantity($counter, currentValue);
                    return;
                }

                queueSync($counter, currentValue + 1, currentValue);
            },
        );

        $(document).off(
            "click" + settings.namespace,
            settings.counterSelector + " " + Selectors.ButtonCounterMinus,
        );
        $(document).on(
            "click" + settings.namespace,
            settings.counterSelector + " " + Selectors.ButtonCounterMinus,
            function () {
                const $counter = $(this).closest(settings.counterSelector);
                const currentValue = getQuantity($counter);

                if (currentValue <= MIN_QUANTITY) {
                    setQuantity($counter, currentValue);
                    return;
                }

                queueSync($counter, currentValue - 1, currentValue);
            },
        );

        $(document).off(
            "keydown" + settings.namespace,
            settings.counterSelector + " " + Selectors.ItemCountDisplay,
        );
        $(document).on(
            "keydown" + settings.namespace,
            settings.counterSelector + " " + Selectors.ItemCountDisplay,
            function (e) {
                if (e.key === "Enter") {
                    e.preventDefault();
                    $(this).trigger("change");
                }
            },
        );

        $(document).off(
            "change" + settings.namespace,
            settings.counterSelector + " " + Selectors.ItemCountDisplay,
        );
        $(document).on(
            "change" + settings.namespace,
            settings.counterSelector + " " + Selectors.ItemCountDisplay,
            function () {
                const $counter = $(this).closest(settings.counterSelector);
                const currentSyncedQuantity = clampQuantity(
                    Number.parseInt(
                        $counter.data(BindCounterDataKey.LastSyncedQuantity),
                        10,
                    ),
                );
                const requestedQuantity = getQuantity($counter);

                if (requestedQuantity === currentSyncedQuantity) {
                    setQuantity($counter, requestedQuantity);
                    if (typeof settings.onQuantityApplied === "function") {
                        settings.onQuantityApplied($counter, requestedQuantity);
                    }
                    return;
                }

                queueSync($counter, requestedQuantity, currentSyncedQuantity);
            },
        );

        $(document).off("change" + settings.namespace, settings.giftSelector);
        $(document).on(
            "change" + settings.namespace,
            settings.giftSelector,
            function () {
                const $card = $(this).closest(settings.cardSelector);
                const $counter = findCounterInCard($card);

                if ($counter.length === 0) {
                    console.error(
                        "Unable to find cart item counter for gift selector change. cardSelector:",
                        settings.cardSelector,
                        "giftSelector:",
                        settings.giftSelector,
                    );
                    return;
                }

                const isGiftChecked = $(this).is(":checked");
                $card.attr("data-isgift", isGiftChecked ? "true" : "false");
                $card.data("isgift", isGiftChecked);

                const currentSyncedQuantity = clampQuantity(
                    Number.parseInt(
                        $counter.data(BindCounterDataKey.LastSyncedQuantity),
                        10,
                    ),
                );
                const currentQuantity = getQuantity($counter);
                queueSync(
                    $counter,
                    currentQuantity,
                    currentSyncedQuantity,
                    true,
                );
            },
        );

        return {
            initialize: initialize,
        };
    }

    // #endregion
}
globalThis.SaCartUtils = SaCartUtils;
